/* eslint-disable jsx-a11y/anchor-is-valid */
import { IonButton, IonItem, IonRouterLink, IonSelect, IonSelectOption, IonToast, useIonModal } from '@ionic/react';
import { FeatureFlagsLookup, Roles, User, UserRolesLookup, addDays, getNow, objectEquals } from 'model';
import { useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useCountPostsByAuthorQuery } from '../../api/PostApi';
import { useGetReportsByUserQuery } from '../../api/ReportApi';
import {
	useGetAdminProfileQuery,
	useSetActiveMutation,
	useSuspendUserMutation,
	useUnSuspendUserMutation,
	useUpdateUserMutation
} from '../../api/UserApi';
import { Crumbs } from '../../components/Forms/Crumbs';
import { EditableField } from '../../components/Forms/EditableField';
import { SendPasswordReset } from '../../components/Forms/SendPasswordReset';
import { Header } from '../../components/Header';
import { IconButton } from '../../components/IconButton';
import {
	IconAdd,
	IconArrowBack,
	IconCheckCircle,
	IconDeactivate,
	IconPause,
	IconReport
} from '../../components/QuirkIcons';
import { QuirkIonContent } from '../../components/QuirkIonContent';
import { QuirkIonPage } from '../../components/QuirkIonPage';
import { AdminUserMenuPayments } from '../../components/admin/AdminUserMenuPayments.js';
import { UserItem } from '../../components/admin/UserItem';
import { UserEditRules } from '../../formConfigs/EditUserFieldsConfig';
import { useFormValues } from '../../hooks/useFormValues';
import { useLoading } from '../../hooks/useQuirkLoading';
import { useUserHasRole } from '../../hooks/useUserHasRole';
import { checkSuspended } from '../../util/isSuspended';
import { Confirmation } from '../Confirmation';
import { ActivateModal } from './ActivateModal.js';
import { AdminOnly } from './AdminOnly';
import { ReportList } from './ReportList';
import { SuspendModal } from './SuspendModal.js';
import { UserStatus } from './UserStatus.js';

// styles
// eslint-disable-next-line css-modules/no-unused-class
import settingsMenuStyles from '../../components/SettingsMenu.module.css';
import styles from './AdminUserMenu.module.css';
import { Badge } from '../../components/Badge.js';

const AllUserRoles = {
	...UserRolesLookup,
	...FeatureFlagsLookup
};
export const AdminUserMenu = () => {
	const { userid } = useParams<{ userid: User['_id'] }>();
	const admin = useUserHasRole('admin');
	const history = useHistory();
	// eslint-disable-next-line no-undef
	const adminSelectRef = useRef<HTMLIonSelectElement>(null);
	const [setActive] = useSetActiveMutation();
	const { data: userReports } = useGetReportsByUserQuery({ user: userid }, { skip: !admin });
	const [suspendUser] = useSuspendUserMutation();
	const [unSuspendUser] = useUnSuspendUserMutation();
	const { data: user } = useGetAdminProfileQuery(userid, { skip: !admin });
	const [suspendTimes, setSuspendTimes] = useState<string>('1');
	const [updateCap, setUpdateCap] = useState<boolean>(false);
	const [selectedRoles, setSelectedRoles] = useState<Roles[]>();
	const { data: totalPosts } = useCountPostsByAuthorQuery(userid);
	const [trigger, { isLoading: updateInProgress, isError: updateIsError, isSuccess: updateIsSuccess }] =
		useUpdateUserMutation();
	useLoading({
		isLoading: updateInProgress,
		message: 'Saving...'
	});
	const { setValues, formValues, stateDataObject: updatedUser } = useFormValues(UserEditRules, user, objectEquals);
	const updateRoles = (updatedValues: string[]) => {
		const userWithNewRoles = { ...updatedUser };
		userWithNewRoles.roles = updatedValues as Roles[];
		trigger({ user: userWithNewRoles as User });
		setSelectedRoles(userWithNewRoles.roles);
	};
	const onRoleSelect = (e: CustomEvent) => {
		if (e.detail.value && !updateCap) {
			const selectedOptions: Roles[] = e.detail.value;
			setUpdateCap(true);
			updateRoles(selectedOptions);
		}
	};
	const addRolesHandle = () => {
		setUpdateCap(false);
		adminSelectRef.current && adminSelectRef.current.click();
	};

	useEffect(() => {
		if (user?.roles) {
			setSelectedRoles(user.roles);
		}
	}, [user?.roles]);

	const engage = (action: string) => {
		action === 'suspend' ? presentSuspendModal() : presentActivateModal();
	};

	const timeConverter = (val: string) => {
		return addDays(getNow(), Number(val));
	};

	const getActivateAction = () => {
		if (user) {
			dismissActivateModal();
			setActive({ userid: user._id, active: !user.active });
		}
	};

	const getSuspendAction = () => {
		if (user) {
			dismissSuspendModal();
			if (!checkSuspended(user)) {
				suspendUser({ userid: user._id, suspendedUntil: timeConverter(suspendTimes) });
			} else {
				unSuspendUser({ userid: user._id });
			}
		}
	};

	const [presentSuspendModal, dismissSuspendModal] = useIonModal(Confirmation, {
		onDismiss: () => dismissSuspendModal(),
		action: () => getSuspendAction(),
		actionLabel: checkSuspended(user) ? 'Unsuspend User' : 'Suspend User',
		dismissLabel: 'Cancel',
		content: (
			<SuspendModal
				user={user}
				setSuspendTimes={setSuspendTimes}
				suspendTimes={suspendTimes}
				isSuspended={checkSuspended(user)}
			/>
		)
	});

	const [presentActivateModal, dismissActivateModal] = useIonModal(Confirmation, {
		onDismiss: () => dismissActivateModal(),
		action: () => getActivateAction(),
		actionLabel: user?.active ? 'Deactivate User' : 'Activate User',
		dismissLabel: 'Cancel',
		content: <ActivateModal user={user} />
	});

	// Report filter page
	const [presentReportFilter, dismissReporterListByUser] = useIonModal(ReportList, {
		user: user?._id,
		onDismiss: () => dismissReporterListByUser(),
		modal: true
	});

	return (
		<QuirkIonPage name="Admin User Menu" data={{ user: userid }}>
			<>
				<Header
					title={'Settings'}
					left={<IconButton icon={<IconArrowBack />} onClick={() => history.go(-1)} />}
				/>
				<QuirkIonContent>
					<AdminOnly>
						<>
							{user && formValues && (
								<>
									<IonSelect
										ref={adminSelectRef}
										onIonChange={e => Array.isArray(e.detail.value) && onRoleSelect(e)}
										multiple={true}
										value={selectedRoles}
										style={{ display: 'none' }}
									>
										{Object.values(AllUserRoles).map((role: string, i) => {
											return (
												<IonSelectOption value={role} key={role + i} disabled={role === 'user'}>
													{Object.keys(AllUserRoles)[i]}
												</IonSelectOption>
											);
										})}
									</IonSelect>

									<UserStatus user={user} isSuspended={checkSuspended(user)} />

									<UserItem user={user} />
									<div className={styles.fixedfieldContainer}>
										<EditableField
											id="username"
											setValues={setValues}
											onChange={() => trigger({ user: updatedUser as User })}
											field={formValues.username}
											label="Username"
										/>
										<EditableField
											id="description"
											setValues={setValues}
											onChange={() => trigger({ user: updatedUser as User })}
											field={formValues.description}
											label="Description"
										/>
										<EditableField
											id="email"
											setValues={setValues}
											onChange={() => trigger({ user: updatedUser as User })}
											field={formValues.email}
											label="Email"
										/>
										<div className={styles.formRowWithAction}>
											<Crumbs
												items={user?.roles as string[]}
												label="Roles"
												onDelete={updateRoles}
												lookup={AllUserRoles}
											/>
											<div className={styles.crumbsActionContainer}>
												<IconButton icon={<IconAdd />} onClick={addRolesHandle} />
											</div>
										</div>
										<div className="space20" />
										{totalPosts && (
											<IonRouterLink routerLink={`/main/latest/${userid}`}>
												<IonButton
													color="primary"
													fill="solid"
													size="default"
													className="radius8"
													expand="block"
												>
													View {totalPosts.response} posts
												</IonButton>
											</IonRouterLink>
										)}
										<div className="space20" />
									</div>
									<AdminUserMenuPayments user={user} />
									<div className="space10" />
									<IonItem className={settingsMenuStyles.ionItem}>
										<SendPasswordReset user={user} />
									</IonItem>
									{!checkSuspended(user) && (
										<IonItem className={settingsMenuStyles.ionItem}>
											<div
												className={settingsMenuStyles.menuItemContainer}
												onClick={() => engage('activate')}
											>
												{user.active ? <IconDeactivate /> : <IconCheckCircle />}

												<div className={settingsMenuStyles.menuItemLabel}>
													{user.active ? 'Deactivate User' : 'Activate User'}
												</div>
											</div>
										</IonItem>
									)}
									{user?.active && (
										<IonItem className={settingsMenuStyles.ionItem}>
											<div
												className={settingsMenuStyles.menuItemContainer}
												onClick={() => engage('suspend')}
											>
												{checkSuspended(user) ? <IconCheckCircle /> : <IconPause />}

												<div className={settingsMenuStyles.menuItemLabel}>
													{checkSuspended(user) ? 'Unsuspend User' : 'Suspend User'}
												</div>
											</div>
										</IonItem>
									)}

									<IonItem className={settingsMenuStyles.ionItem}>
										<div
											className={settingsMenuStyles.menuItemContainer}
											onClick={() => presentReportFilter()}
										>
											<IconReport />
											<div className={settingsMenuStyles.menuItemLabelWithJewel}>
												<div className={settingsMenuStyles.menuItemLabel}>
													Review Content Reports
												</div>
												{userReports && (
													<Badge
														type={userReports.length > 0 ? 'alert' : 'info'}
														content={userReports.length}
													/>
												)}
											</div>
										</div>
									</IonItem>
								</>
							)}
						</>
					</AdminOnly>
				</QuirkIonContent>
			</>
			<IonToast
				isOpen={updateIsError}
				duration={1500}
				message="Error updating user"
				position="top"
				color="danger"
			/>
			<IonToast isOpen={updateIsSuccess} duration={1500} message="User updated" position="top" color="success" />
		</QuirkIonPage>
	);
};
